// src/components/RetaBracketModal.jsx

import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  VStack, HStack, Text, Heading, Box, Tag, Divider,
  SimpleGrid, Spinner, Center, Collapse, useDisclosure, Button
} from '@chakra-ui/react';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { useAuthCliente } from '../context/AuthClienteContext';
import { getPhaseName } from '../utils/tournamentLogic';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

export default function RetaBracketModal({ isOpen, onClose, tournament }) {
  const { currentUser } = useAuthCliente();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen: isParticipantsOpen, onToggle: onParticipantsToggle } = useDisclosure();
  
  // Efecto para buscar los nombres de los participantes
  useEffect(() => {
    const fetchParticipants = async () => {
      if (!isOpen || !tournament?.participants || tournament.participants.length === 0) {
        setParticipants([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const playerIds = tournament.participants;
        const playersData = [];
        const chunks = [];
        for (let i = 0; i < playerIds.length; i += 10) {
          chunks.push(playerIds.slice(i, i + 10));
        }
        const ref = collection(db, 'companies', currentUser.companyId, 'end_customers');
        for (const chunk of chunks) {
          if (chunk.length > 0) {
            const q = query(ref, where(documentId(), 'in', chunk));
            const snap = await getDocs(q);
            snap.forEach(doc => {
              playersData.push(doc.data().name);
            });
          }
        }
        setParticipants(playersData);
      } catch (error) {
        console.error("Error fetching participants:", error);
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchParticipants();
  }, [tournament, currentUser.companyId, isOpen]);


  if (!tournament) return null;

  // Los datos del cuadro vienen directamente del objeto 'tournament'
  const rounds = tournament.rounds ? Object.values(tournament.rounds) : [];
  
  // Definimos una configuración de puntos por defecto para las retas si no existe
  const pointConfig = tournament.pointConfig || {
      winner: 40,
      finalist: 25,
      semiFinalist: 15,
      quarterFinalist: 5,
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{tournament.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
            {loading ? <Center h="full"><Spinner /></Center> :
            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} h="full">
                {/* Columna de Detalles (Lista de Participantes) */}
                <VStack align="stretch" spacing={4} p={4} bg="gray.50" borderRadius="lg">
                    <Heading size="lg">Detalles</Heading>
                    <HStack justify="space-between">
                        <Text fontWeight="bold">Fecha del Evento:</Text>
                        <Text>{tournament.createdAt?.toDate().toLocaleDateString('es-ES')}</Text>
                    </HStack>
                    <HStack justify="space-between"><Text fontWeight="bold">Tipo:</Text><Tag colorScheme="purple">Reta</Tag></HStack>
                    <Divider />
                    <HStack justify="space-between" align="center">
                        <Heading size="md">Participantes ({participants.length})</Heading>
                        <Button size="sm" onClick={onParticipantsToggle}>
                        {isParticipantsOpen ? 'Ocultar' : 'Mostrar'}
                        </Button>
                    </HStack>
                    <Collapse in={isParticipantsOpen} animateOpacity>
                        <Box overflowY="auto" maxH="calc(100vh - 450px)" p={2} borderWidth="1px" bg="white" borderRadius="md">
                        {participants.map((name, index) => <Text key={`${name}-${index}`}>{name}</Text>)}
                        </Box>
                    </Collapse>
                </VStack>

                {/* Columna del Cuadro de Resultados */}
                <VStack align="stretch" spacing={4} p={4} gridColumn={{ lg: "span 2" }}>
                    <Heading size="lg">Cuadro de Resultados</Heading>
                    <Box overflowY="auto" h="calc(100vh - 200px)">
                        {rounds.length === 0 && <Center p={5}><Text>No hay un cuadro de resultados para esta reta.</Text></Center>}
                        {rounds.map((round, roundIndex) => {
                        const phaseName = getPhaseName(roundIndex.toString(), tournament.participants?.length || 2);
                        return (
                        <Box key={roundIndex} mb={6}>
                            <HStack mb={3}>
                                <Heading size="md">{phaseName}</Heading>
                            </HStack>
                            {round.map((match, matchIdx) => {
                            const winnerId = tournament.results?.[roundIndex]?.[matchIdx];
                            const matchScores = tournament.scores?.[roundIndex]?.[matchIdx] || [];
                            const matchDate = tournament.matchDates?.[roundIndex]?.[matchIdx]?.toDate();

                            // Lógica para determinar los puntos
                            let points = { winner: pointConfig.winner, loser: pointConfig.finalist };
                            if (phaseName === 'Semifinales') points = { winner: 'Avanza', loser: pointConfig.semiFinalist };
                            if (phaseName === 'Cuartos de Final') points = { winner: 'Avanza', loser: pointConfig.quarterFinalist };

                            return (
                                <VStack key={matchIdx} p={3} borderWidth="1px" borderRadius="md" bg="white" align="stretch" mb={2} boxShadow="sm">
                                <HStack justify="space-between">
                                    <VStack align="start" spacing={1} flex="1">
                                        <HStack>
                                            <Text fontWeight={winnerId === match.player1?.id ? 'bold' : 'normal'} color={winnerId === match.player1?.id ? 'green.600' : 'inherit'}>{match.player1?.name || '(Por definir)'}</Text>
                                            {winnerId && <Text fontSize="xs" color="gray.500">({winnerId === match.player1?.id ? `${points.winner} pts` : `${points.loser} pts`})</Text>}
                                        </HStack>
                                        <HStack>
                                            <Text fontWeight={winnerId === match.player2?.id ? 'bold' : 'normal'} color={winnerId === match.player2?.id ? 'green.600' : 'inherit'}>{match.player2?.name || '(Por definir)'}</Text>
                                            {winnerId && <Text fontSize="xs" color="gray.500">({winnerId === match.player2?.id ? `${points.winner} pts` : `${points.loser} pts`})</Text>}
                                        </HStack>
                                    </VStack>
                                    <HStack spacing={3}>
                                    {matchScores.length > 0 ? (
                                        matchScores.map((set, i) => (
                                        <VStack key={i} spacing={1} bg="gray.100" p={1} borderRadius="sm">
                                            <Text fontSize="sm" lineHeight={1} fontWeight={parseInt(set.player1_score) > parseInt(set.player2_score) ? 'bold' : 'normal'}>
                                            {set.player1_score}
                                            </Text>
                                            <Divider/>
                                            <Text fontSize="sm" lineHeight={1} fontWeight={parseInt(set.player2_score) > parseInt(set.player1_score) ? 'bold' : 'normal'}>
                                            {set.player2_score}
                                            </Text>
                                        </VStack>
                                        ))
                                    ) : <Tag>Resultado no disponible</Tag>}
                                    </HStack>
                                </HStack>
                                {matchDate && (
                                    <Text fontSize="xs" color="gray.500" pt={2} borderTopWidth="1px" borderColor="gray.200" mt={2}>
                                    Jugado el: {format(matchDate, "d MMM, yyyy 'a las' HH:mm'h'", { locale: es })}
                                    </Text>
                                )}
                                </VStack>
                            );
                            })}
                        </Box>
                        )})}
                    </Box>
                </VStack>
            </SimpleGrid>
            }
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}